<template>
    <Teleport to="body">
        <!-- Cart Toggle Button -->
        <button
            v-if="!isOpen"
            @click="openCart"
            class="fixed top-6 right-6 z-40 bg-[var(--primary-400)] hover:bg-[var(--primary-700)] text-white rounded-full p-2.5 shadow-lg transition-all hover:scale-110 flex items-center justify-center"
        >
            <i class="fa-fw fa-light fa-sharp fa-shopping-cart text-base"></i>
            <span
                v-if="itemCount > 0"
                class="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center"
            >
                {{ itemCount }}
            </span>
        </button>

        <!-- Cart Overlay -->
        <div
            v-if="isOpen"
            class="fixed inset-0 z-50 flex items-end md:items-end md:justify-end p-4 pointer-events-none"
        >
            <!-- Cart Panel -->
            <div
                class="bg-white dark:bg-neutral-800 rounded-t-2xl md:rounded-2xl w-full md:w-[300px] max-h-[85vh] flex flex-col shadow-xl md:shadow-2xl pointer-events-auto"
            >
                <!-- Cart Header -->
                <div class="flex items-center justify-between p-4 border-b border-neutral-200 dark:border-neutral-700">
                    <h2 class="text-xl font-bold text-neutral-900 dark:text-white">Cart</h2>
                    <button
                        @click="closeCart"
                        class="text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200"
                    >
                        <i class="fa-fw fa-light fa-sharp fa-times text-lg"></i>
                    </button>
                </div>

                <!-- Free Shipping Progress -->
                <div v-if="items.length > 0 && freeShippingThreshold && freeShippingThreshold > 0" class="px-4 pt-4 pb-4 border-b border-neutral-200 dark:border-neutral-700">
                    <div class="space-y-2">
                        <div class="flex items-center justify-between text-sm">
                            <span class="text-neutral-600 dark:text-neutral-400">
                                <span v-if="remainingUntilFreeShipping > 0">
                                    Add ${{ formatPrice(remainingUntilFreeShipping) }} more for free shipping
                                </span>
                                <span v-else class="text-green-600 dark:text-green-400 font-semibold">
                                    🎉 You've earned free shipping!
                                </span>
                            </span>
                        </div>
                        <div class="w-full bg-neutral-200 dark:bg-neutral-700 rounded-full h-2 overflow-hidden">
                            <div 
                                class="h-full bg-[var(--primary-400)] transition-all duration-300 rounded-full"
                                :style="{ width: freeShippingProgress + '%' }"
                            ></div>
                        </div>
                    </div>
                </div>

                <!-- Cart Items -->
                <div class="flex-1 overflow-y-auto p-4">
                    <div v-if="items.length === 0" class="text-center py-8">
                        <i class="fa-fw fa-light fa-sharp fa-shopping-cart text-3xl text-neutral-400 mb-3"></i>
                        <p class="text-sm text-neutral-600 dark:text-neutral-400">Your cart is empty</p>
                    </div>

                    <div v-else class="space-y-3">
                        <div
                            v-for="item in items"
                            :key="item.id"
                            class="flex items-center gap-3 p-3 border border-neutral-200 dark:border-neutral-700 rounded-lg"
                        >
                            <div class="flex-1 min-w-0">
                                <h3 class="font-semibold text-sm text-neutral-900 dark:text-white truncate">{{ item.name }}</h3>
                                <div class="flex items-center gap-3 mt-2">
                                    <div class="flex items-center gap-1.5">
                                        <button
                                            @click="decreaseQuantity(item.id)"
                                            :disabled="checkoutInitiated"
                                            :class="[
                                                'w-7 h-7 flex items-center justify-center border rounded',
                                                checkoutInitiated 
                                                    ? 'border-neutral-200 dark:border-neutral-700 text-neutral-400 dark:text-neutral-600 cursor-not-allowed' 
                                                    : 'border-neutral-300 dark:border-neutral-600 hover:bg-neutral-100 dark:hover:bg-neutral-800 text-neutral-900 dark:text-white'
                                            ]"
                                        >
                                            <i class="fa-fw fa-light fa-sharp fa-minus text-xs"></i>
                                        </button>
                                        <span class="w-6 text-center text-sm text-neutral-900 dark:text-white">{{ item.quantity }}</span>
                                        <button
                                            @click="increaseQuantity(item.id)"
                                            :disabled="checkoutInitiated"
                                            :class="[
                                                'w-7 h-7 flex items-center justify-center border rounded',
                                                checkoutInitiated 
                                                    ? 'border-neutral-200 dark:border-neutral-700 text-neutral-400 dark:text-neutral-600 cursor-not-allowed' 
                                                    : 'border-neutral-300 dark:border-neutral-600 hover:bg-neutral-100 dark:hover:bg-neutral-800 text-neutral-900 dark:text-white'
                                            ]"
                                        >
                                            <i class="fa-fw fa-light fa-sharp fa-plus text-xs"></i>
                                        </button>
                                    </div>
                                    <span class="font-semibold text-sm text-neutral-900 dark:text-white">
                                        ${{ formatPrice(item.price * item.quantity) }}
                                    </span>
                                </div>
                            </div>
                            <button
                                @click="removeItem(item.id)"
                                :disabled="checkoutInitiated"
                                :class="[
                                    'w-7 h-7 flex items-center justify-center border rounded flex-shrink-0',
                                    checkoutInitiated 
                                        ? 'border-neutral-200 dark:border-neutral-700 text-neutral-400 dark:text-neutral-600 cursor-not-allowed' 
                                        : 'border-red-300 dark:border-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 text-red-500 hover:text-red-700'
                                ]"
                            >
                                <i class="fa-fw fa-light fa-sharp fa-trash text-sm"></i>
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Bundle Discount Upsell -->
                <div v-if="showBundleUpsell" class="px-4 pt-4 pb-4 border-t border-b border-neutral-200 dark:border-neutral-700">
                    <div class="space-y-2">
                        <div class="flex items-center justify-between text-sm">
                            <span class="text-neutral-600 dark:text-neutral-400">
                                Bundle All Products for {{ effectiveBundleDiscountPercent }}% Discount
                            </span>
                            <button
                                @click="applyBundleDiscount"
                                :disabled="checkoutInitiated"
                                :class="[
                                    'text-white text-xs font-semibold px-3 py-1.5 rounded transition-colors',
                                    checkoutInitiated 
                                        ? 'bg-neutral-400 cursor-not-allowed' 
                                        : 'bg-[var(--primary-400)] hover:bg-[var(--primary-700)]'
                                ]"
                            >
                                Apply
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Cart Footer -->
                <div v-if="items.length > 0" class="border-t border-neutral-200 dark:border-neutral-700 p-4 space-y-3">
                    <!-- Bundle Discount Section (shown when they qualify) -->
                    <div v-if="qualifiesForBundleDiscount && effectiveBundleDiscountPercent && effectiveBundleDiscountPercent > 0" class="space-y-2 pb-3 border-b border-neutral-200 dark:border-neutral-700">
                        <div v-if="!bundleDiscountApplied" class="flex items-center justify-between text-sm">
                            <span class="text-neutral-600 dark:text-neutral-400">
                                Bundle Discount Available ({{ effectiveBundleDiscountPercent }}%)
                            </span>
                            <button
                                @click="applyBundleDiscount"
                                :disabled="checkoutInitiated"
                                :class="[
                                    'text-white text-xs font-semibold px-3 py-1.5 rounded transition-colors',
                                    checkoutInitiated 
                                        ? 'bg-neutral-400 cursor-not-allowed' 
                                        : 'bg-[var(--primary-400)] hover:bg-[var(--primary-700)]'
                                ]"
                            >
                                Apply
                            </button>
                        </div>
                        <div v-if="bundleDiscountApplied" class="space-y-1 text-sm">
                            <div class="flex items-center justify-between text-neutral-600 dark:text-neutral-400">
                                <span>Subtotal:</span>
                                <span>${{ formatPrice(subtotal) }}</span>
                            </div>
                            <div class="flex items-center justify-between text-green-600 dark:text-green-400">
                                <span>Bundle Discount ({{ effectiveBundleDiscountPercent }}%):</span>
                                <span>-${{ formatPrice(bundleDiscountAmount) }}                                </span>
                            </div>
                        </div>
                    </div>
                    <div v-if="showShippingMessage" class="text-xs text-neutral-500 dark:text-neutral-400 text-center pb-1">
                        Shipping calculated at checkout
                    </div>
                    <div class="flex items-center justify-between font-semibold">
                        <span class="text-neutral-900 dark:text-white">Total:</span>
                        <span class="text-neutral-900 dark:text-white">${{ formatPrice(total) }}</span>
                    </div>
                    <checkout-button />
                </div>
            </div>
        </div>
    </Teleport>
</template>

<script>
import { cartStore } from '../store/cart';

export default {
    name: 'Cart',
    props: {
        freeShippingThreshold: {
            type: Number,
            default: null,
        },
        bundleDiscountPercent: {
            type: Number,
            default: null,
        },
    },
    data() {
        return {
            ecommProducts: [],
            ecommBundleDiscountPercent: null,
        };
    },
    mounted() {
        // Initialize e-comm config from data attribute
        const ecommConfigEl = document.querySelector('[data-ecomm-config]');
        if (ecommConfigEl) {
            try {
                const config = JSON.parse(ecommConfigEl.getAttribute('data-ecomm-config'));
                window.app = window.app || {};
                window.app.ecommProducts = config.products || [];
                window.app.ecommBlockId = config.blockId || null;
                window.app.ecommPageUuid = config.pageUuid || null;
                window.app.bundleDiscountPercent = config.bundleDiscountPercent || null;
                
                // Store in component data for reactivity
                this.ecommProducts = config.products || [];
                this.ecommBundleDiscountPercent = config.bundleDiscountPercent || null;
            } catch (e) {
                console.error('Failed to parse e-comm config:', e);
            }
        }
        
        // Also check window.app in case it was set elsewhere
        if (window.app?.ecommProducts) {
            this.ecommProducts = window.app.ecommProducts;
        }
        if (window.app?.bundleDiscountPercent) {
            this.ecommBundleDiscountPercent = window.app.bundleDiscountPercent;
        }
    },
    computed: {
        isOpen() {
            return cartStore.state.isOpen;
        },
        items() {
            return cartStore.state.items;
        },
        total() {
            return cartStore.getTotal();
        },
        subtotal() {
            return cartStore.state.items.reduce((total, item) => {
                return total + (parseFloat(item.price || 0) * item.quantity);
            }, 0);
        },
        bundleTotal() {
            if (!this.bundleDiscountPercent || this.bundleDiscountPercent <= 0) {
                return this.subtotal;
            }
            const discount = this.subtotal * (this.bundleDiscountPercent / 100);
            return this.subtotal - discount;
        },
        bundleDiscountAmount() {
            const bundlePercent = this.effectiveBundleDiscountPercent;
            if (!bundlePercent || bundlePercent <= 0) {
                return 0;
            }
            return this.subtotal * (bundlePercent / 100);
        },
        bundleDiscountApplied() {
            return cartStore.state.bundleDiscountApplied;
        },
        checkoutInitiated() {
            return cartStore.state.checkoutInitiated;
        },
        effectiveBundleDiscountPercent() {
            // Use prop first, then component data, then window.app fallback
            return this.bundleDiscountPercent || this.ecommBundleDiscountPercent || window.app?.bundleDiscountPercent || null;
        },
        showBundleUpsell() {
            // Get bundle discount percent from prop or fallback
            const bundlePercent = this.effectiveBundleDiscountPercent;
            
            // Only show if bundle discount is configured and discount hasn't been applied
            if (!bundlePercent || bundlePercent <= 0) {
                return false;
            }
            
            // Don't show if bundle discount is already applied
            if (this.bundleDiscountApplied) {
                return false;
            }
            
            // User must have at least one product in cart
            if (this.items.length === 0) {
                return false;
            }
            
            // Get all available product IDs from e-comm block
            // Always check both data property and window.app to get the latest value
            // This ensures reactivity even if window.app is updated after mount
            const allProducts = this.ecommProducts.length > 0 
                ? this.ecommProducts 
                : (window.app?.ecommProducts || []);
            const allProductIds = allProducts.map(p => p && p.id ? p.id : null).filter(Boolean);
            
            // Get unique cart product IDs
            const cartProductIds = [...new Set(this.items.map(item => item.id).filter(Boolean))];
            
            // Only show upsell if there are multiple products available AND user doesn't have all products
            const hasMultipleProducts = allProductIds.length >= 2;
            const hasAllProducts = allProductIds.length > 0 && cartProductIds.length >= allProductIds.length;
            
            return hasMultipleProducts && !hasAllProducts;
        },
        qualifiesForBundleDiscount() {
            // Check if user has all products available
            const bundlePercent = this.effectiveBundleDiscountPercent;
            if (!bundlePercent || bundlePercent <= 0) {
                return false;
            }
            
            // Get all available product IDs from e-comm block
            const allProducts = this.ecommProducts.length > 0 
                ? this.ecommProducts 
                : (window.app?.ecommProducts || []);
            const allProductIds = allProducts.map(p => p && p.id ? p.id : null).filter(Boolean);
            
            // Get unique cart product IDs
            const cartProductIds = [...new Set(this.items.map(item => item.id).filter(Boolean))];
            
            // Qualifies if there are multiple products and user has all of them
            return allProductIds.length >= 2 && cartProductIds.length >= allProductIds.length;
        },
        itemCount() {
            return cartStore.getItemCount();
        },
        remainingUntilFreeShipping() {
            if (!this.freeShippingThreshold || this.freeShippingThreshold <= 0) {
                return 0;
            }
            const remaining = this.freeShippingThreshold - this.total;
            return remaining > 0 ? remaining : 0;
        },
        freeShippingProgress() {
            if (!this.freeShippingThreshold || this.freeShippingThreshold <= 0) {
                return 0;
            }
            const progress = (this.total / this.freeShippingThreshold) * 100;
            return Math.min(progress, 100);
        },
        showShippingMessage() {
            // Show shipping message if:
            // 1. Free shipping threshold is configured
            // 2. Total is less than threshold
            // 3. There are items in cart
            if (!this.freeShippingThreshold || this.freeShippingThreshold <= 0) {
                return false;
            }
            return this.items.length > 0 && this.total < this.freeShippingThreshold;
        },
    },
    watch: {
        // Watch items and automatically remove bundle discount if they no longer qualify
        items: {
            handler() {
                // If bundle discount is applied but they no longer qualify, remove it
                if (this.bundleDiscountApplied && !this.qualifiesForBundleDiscount) {
                    cartStore.removeBundleDiscount();
                }
            },
            deep: true,
        },
        // Also watch qualifiesForBundleDiscount directly
        qualifiesForBundleDiscount(newValue) {
            // If they no longer qualify and discount is applied, remove it
            if (!newValue && this.bundleDiscountApplied) {
                cartStore.removeBundleDiscount();
            }
        },
    },
    methods: {
        openCart() {
            cartStore.openCart();
        },
        closeCart() {
            cartStore.closeCart();
        },
        removeItem(productId) {
            cartStore.removeItem(productId);
        },
        increaseQuantity(productId) {
            const item = cartStore.state.items.find(item => item.id === productId);
            if (item) {
                cartStore.updateQuantity(productId, item.quantity + 1);
            }
        },
        decreaseQuantity(productId) {
            const item = cartStore.state.items.find(item => item.id === productId);
            if (item && item.quantity > 1) {
                cartStore.updateQuantity(productId, item.quantity - 1);
            } else if (item) {
                cartStore.removeItem(productId);
            }
        },
        formatPrice(price) {
            return parseFloat(price).toFixed(2);
        },
        applyBundleDiscount() {
            // Get all available products from e-comm block
            const allProducts = this.ecommProducts.length > 0 
                ? this.ecommProducts 
                : (window.app?.ecommProducts || []);
            
            // Get current cart product IDs
            const cartProductIds = this.items.map(item => item.id).filter(Boolean);
            
            // Find products that are not in the cart
            const missingProducts = allProducts.filter(product => {
                return product.id && !cartProductIds.includes(product.id);
            });
            
            // Add missing products to cart without saving (skipSave = true)
            missingProducts.forEach(product => {
                cartStore.addItem({
                    id: product.id,
                    name: product.name || '',
                    price: product.price || 0,
                    description: product.description || '',
                    images: product.images || [],
                }, true); // Skip save to avoid race condition
            });
            
            // Apply the bundle discount
            cartStore.applyBundleDiscount();
            
            // Save cart to Google Sheets once after all products are added and discount is applied
            cartStore.saveCartToSheets(false);
        },
        removeBundleDiscount() {
            cartStore.removeBundleDiscount();
        },
    },
};
</script>

<style scoped>
</style>

